/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios';
import {
  IAsyncClientRegistration,
  setAsyncClientRegister
} from '@ion/core/src/services/AsyncClientManager';
import APM from '../APM';
import {IPerformanceMonitor} from '../types/types';
import getInitializationMonitor from './InitializationPerformanceMonitor';
import getPageMonitor from './PagePerformanceMonitor';

type ServiceMonitorParam = AxiosRequestConfig | AxiosResponse | AxiosError;

/**
 * @internal
 *
 * The `ServiceMonitor` API provides an automated mechanism for monitoring
 * the requests and responses of Service client instances in the context of
 * the Application Performance Monitoring API.
 *
 * When APM is enabled, clients can invoke the `register` method of the
 * `ServiceMonitor` API to automatically intercept requests and responses
 * for performing timing metrics of matched service API URLs, this allowing
 * for automating the process of measuring specific async service requests
 * and responses of a given Performance Monitor instance which is observing
 * the specific services as part of it's monitoring metrics.
 *
 * This API is primarily intended to be a package level instance, that is,
 * used internally by the underlying PerformanceMonitor instances.
 */
class ServiceMonitor implements IAsyncClientRegistration {
  /**
   * Provides a reference to the service clients which are being observed
   * by all currently running Performance Monitors.
   */
  protected clients: Set<AxiosInstance>;

  /**
   * Provides a reference to the `PerformanceMonitor` instances for which
   * all intercepted service invocations are to be measured.
   */
  protected monitors: IPerformanceMonitor[];

  /**
   * The `ServiceMonitor` constructor initializes the collection of performance
   * monitors which are to automatically measure service calls. By default, the
   * `InitializationPerformanceMonitor` and `PagePerformanceMonitor` instances
   * are assigned to the monitors.
   */
  constructor() {
    this.clients = new Set();
    this.monitors = [
      getInitializationMonitor(),
      getPageMonitor()
    ];
  }

  /**
   * Determines the number of clients which have been registered and returns
   * the result.
   *
   * @returns the number of service clients that have been registered.
   */
  public getClientCount(): number {
    return this.clients.size;
  }

  /**
   * Adds a service client for request / response interceptions to be included
   * in the service metrics of all currently running Performance Monitors.
   *
   * @param instance The Service client which is to have it's requests
   *        and responses intercepted for measurement.
   */
  public register(instance: AxiosInstance) {
    if (!this.clients.has(instance)) {
      this.clients.add(instance);
      this.addRequestInterceptor(instance);
      this.addResponseInterceptor(instance);
    }
  }

  /**
   * Adds a service client request interceptor to be included in the service
   * metrics of all currently running Performance Monitors.
   *
   * @param instance The Service client which is to have it's requests
   *        intercepted for measurement.
   */
  protected addRequestInterceptor(instance: AxiosInstance) {
    instance.interceptors.request.use((request: AxiosRequestConfig) => {
      this.monitorService(request, true);
      return request;
    }, (error: AxiosError) => {
      this.monitorService(error);
      return Promise.reject(error);
    });
  }

  /**
   * Adds a service client response interceptor to be included in the service
   * metrics of all currently running Performance Monitors.
   *
   * @param instance The Service client which is to have it's responses
   *        intercepted for measurement.
   */
  protected addResponseInterceptor(instance: AxiosInstance) {
    instance.interceptors.response.use((response: AxiosResponse) => {
      this.monitorService(response);
      return response;
    }, (error: AxiosError) => {
      this.monitorService(error);
      return Promise.reject(error);
    });
  }

  /**
   * Provides an abstraction for starting or stopping the measurement of
   * a given service for each running performance monitor instance.
   *
   * @param item A service client request, response, or error which is
   *        to have it's measurement started or stopped for all running
   *        service monitors.
   * @param isStart When true, the `startService` method will be invoked
   *        on each running service monitor for the given request url,
   *        otherwise, when `false`, the `stopService` method will be
   *        invoked on each running service monitor for the given response
   *        url. (defaults to `false`)
   */
  protected monitorService(item: ServiceMonitorParam, isStart = false) {
    this.monitors.forEach((monitor: IPerformanceMonitor) => {
      if (monitor.isRunning()) {
        monitor?.[`${isStart ? 'start' : 'stop'}Service`](ServiceMonitor.getService(item));
      }
    });
  };

  /**
   * Provides a utility which determines the url of the given item and returns
   * the `url` path of the given service.
   *
   * @param   item a `ServiceMonitorParam` from which the service url is to be
   *          obtained.
   * @returns The path of the given service url obtained from the `item` param.
   */
  static getService(item: ServiceMonitorParam): string {
    const param = item as any;
    const url   = param?.config?.url || param?.url;
    try {
      return new URL(url).pathname;
    } catch (err) {
      return url || '';
    }
  };
};

/**
 * Provides the single point of entry for referencing the `ServiceMonitor` instance,
 * Note, this is a pseudo-internal API (package-level in the traditional sense) in
 * that the creation of the `ServiceRegister` instance is to be exclusively managed
 * by a single API.
 */
const initializeServiceMonitor = (() => {
  let instance: IAsyncClientRegistration;
  return (): IAsyncClientRegistration => {
    if (APM.isEnabled) {
      if (!instance) {
        instance = new ServiceMonitor();
        setAsyncClientRegister(instance);
      }
    } else {
      instance = undefined;
    }
    return instance;
  };
})();

export default initializeServiceMonitor;
